import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { C, R, font } from '../theme/tokens';
import { navigate, useRoute, type Tab } from '../lib/router';
import { useApp } from '../state/store';

/** Ink ticker band, rotated −1.5° and bleeding off both edges. Paused under reduced motion. */
export function Ticker({ items }: { items: string[] }) {
  const run = items.flatMap((t, i) => [<span key={`t${i}`}>{t}</span>, <span key={`s${i}`} aria-hidden="true" style={{ color: C.rim }}>✦</span>]);
  return (
    <div role="marquee" aria-label={items.join('. ')} style={{ margin: '14px 0 0', background: C.ink, color: C.white, padding: '9px 0', overflow: 'hidden', whiteSpace: 'nowrap', transform: 'rotate(-1.5deg) scale(1.04)' }}>
      <div className="marquee" aria-hidden="true" style={{ display: 'inline-flex', gap: 22, font: font(700, 12), letterSpacing: '.12em' }}>
        {run}
        {run.map((el, i) => <span key={`d${i}`} style={{ display: 'contents' }}>{el}</span>)}
      </div>
    </div>
  );
}

/** Floating circular sticker (B-direction energy). */
export function Sticker({ children, size = 90, bg = C.rim, fg = C.ink, style }: { children: ReactNode; size?: number; bg?: string; fg?: string; style?: CSSProperties }) {
  return (
    <div className="float" aria-hidden="true"
      style={{ position: 'absolute', width: size, height: size, borderRadius: '50%', background: bg, color: fg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', font: font(800, 10, 1.15), letterSpacing: '.06em', zIndex: 2, pointerEvents: 'none', ...style }}>
      {children}
    </div>
  );
}

const NAV: { tab: Tab; label: string }[] = [
  { tab: 'home', label: 'Home' },
  { tab: 'discover', label: 'Discover' },
  { tab: 'create', label: 'Create' },
  { tab: 'calendar', label: 'Calendar' },
  { tab: 'me', label: 'Me' },
];

/** The approved five-button floating nav: Home · Discover · + · Calendar · Me. */
export function BottomNav() {
  const { tab } = useRoute();
  return (
    <nav aria-label="Main" style={{ position: 'absolute', left: 12, right: 12, bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))', height: 70, borderRadius: R.pill, background: C.ink, display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', alignItems: 'center', padding: '0 6px', zIndex: 3 }}>
      {NAV.map(({ tab: t, label }) => {
        const on = tab === t;
        const create = t === 'create';
        return (
          <button key={t} type="button" aria-label={label} aria-current={on ? 'page' : undefined} onClick={() => navigate({ name: t })}
            style={{ height: 58, border: 0, background: 'transparent', color: on ? C.white : C.mutedDark, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, font: font(700, 10, 1), transition: 'color .2s' }}>
            {create ? (
              <span style={{ width: 52, height: 52, borderRadius: R.pill, background: on ? C.white : C.violet, color: on ? C.violet : C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', font: font(600, 26, 1), transition: 'all .2s' }}>+</span>
            ) : (
              label
            )}
          </button>
        );
      })}
    </nav>
  );
}

/** Bottom sheet with scrim, Escape-to-close and initial focus. */
export function Sheet({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <>
      <div className="fade" onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(13,13,18,.55)', zIndex: 6 }} />
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={label} className="sheet-up noscroll"
        style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '92%', overflowY: 'auto', background: C.white, borderRadius: '24px 24px 0 0', padding: '12px 22px calc(30px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: 16, zIndex: 7, outline: 'none' }}>
        <div aria-hidden="true" style={{ width: 40, height: 4, borderRadius: 4, background: C.border, alignSelf: 'center', flex: 'none' }} />
        {children}
      </div>
    </>
  );
}

export function Toast() {
  const { state: { ui } } = useApp();
  if (!ui.toast) return null;
  return (
    <div style={{ position: 'absolute', left: 20, right: 20, bottom: 'calc(98px + env(safe-area-inset-bottom, 0px))', display: 'flex', justifyContent: 'center', zIndex: 9, pointerEvents: 'none' }}>
      <div key={ui.toast.id} role="status" aria-live="polite" className="toast-up"
        style={{ background: C.white, color: C.ink, border: `1px solid ${C.subtle}`, padding: '12px 18px', borderRadius: R.pill, font: font(600, 13, 1.3), textAlign: 'center' }}>
        {ui.toast.text}
      </div>
    </div>
  );
}
